/**
 * Catálogo de los asientos que el sistema genera solo.
 *
 * DESCRIBE lo que hace `integracion.service.ts`; no lo ejecuta. Sirve para que el
 * contador vea, en la pantalla de Mapeo de Cuentas, cómo va a quedar el debe y el
 * haber de cada situación con las cuentas que él mismo mapeó.
 *
 * ⚠️ Si cambiás un `integrar*` de `integracion.service.ts`, actualizá la situación
 * correspondiente acá. `catalogo-asientos.spec.ts` verifica que los conceptos
 * existan y que cada situación cuadre, pero no puede detectar que el código real
 * haya empezado a usar otra cuenta.
 *
 * Los montos son un ejemplo fijo y coherente (una venta de Gs. 1.100.000 con IVA
 * incluido), elegidos para que se vea la aritmética y los dos lados cierren.
 */

export type LadoAsiento = 'DEBE' | 'HABER';

export interface LineaCatalogo {
  lado: LadoAsiento;
  /** Concepto del mapeo (`cont_mapeo_cuentas.concepto`). */
  concepto?: string;
  /** Cuando la cuenta NO sale del mapeo: de dónde sale realmente. */
  cuentaDinamica?: string;
  /**
   * La cuenta se resuelve con datos de la empresa, no con el mapeo. La pantalla
   * usa esto para mostrar las cuentas y categorías reales en vez de un texto.
   */
  fuente?:
    | 'CUENTA_TESORERIA'
    | 'CATEGORIA_TESORERIA'
    | 'CUENTA_TES_ORIGEN'
    | 'CUENTA_TES_DESTINO'
    /** La cuenta la elige la empresa por medio de pago (`cont_mapeo_medio_pago`). */
    | 'MEDIO_PAGO_COBRO'
    | 'MEDIO_PAGO_PAGO';
  etiqueta: string;
  monto: number;
  /** Por qué la línea puede no aparecer en un asiento real. */
  condicion?: string;
}

export interface SituacionCatalogo {
  clave: string;
  grupo: string;
  titulo: string;
  /** Cuándo se dispara. */
  cuando: string;
  /** Documento del ERP que lo genera. */
  documento: string;
  lineas: LineaCatalogo[];
  nota?: string;
}

const IVA_10 = 100_000;
const BASE_10 = 1_000_000;
const TOTAL = BASE_10 + IVA_10;

export const CATALOGO_ASIENTOS: SituacionCatalogo[] = [
  // ── Ventas ────────────────────────────────────────────────────────────────
  {
    clave: 'VENTA_CONTADO',
    grupo: 'Ventas',
    titulo: 'Venta al contado',
    cuando: 'Al aprobarse una factura de venta con condición contado',
    documento: 'Factura de venta',
    lineas: [
      { lado: 'DEBE', concepto: 'CAJA_GENERAL', fuente: 'MEDIO_PAGO_COBRO', etiqueta: 'Cobro en el acto', monto: TOTAL },
      { lado: 'DEBE', concepto: 'CHEQUES_EN_CARTERA', etiqueta: 'Parte cobrada con cheque', monto: 0, condicion: 'Solo si parte se cobró con cheque' },
      { lado: 'HABER', concepto: 'VENTAS_10', etiqueta: 'Venta gravada 10% (sin IVA)', monto: BASE_10 },
      { lado: 'HABER', concepto: 'VENTAS_5', etiqueta: 'Venta gravada 5% (sin IVA)', monto: 0, condicion: 'Solo si la factura tiene ítems al 5%' },
      { lado: 'HABER', concepto: 'VENTAS_EXENTAS', etiqueta: 'Venta exenta', monto: 0, condicion: 'Solo si la factura tiene ítems exentos' },
      { lado: 'HABER', concepto: 'IVA_DEBITO_10', etiqueta: 'IVA débito 10%', monto: IVA_10 },
      { lado: 'HABER', concepto: 'IVA_DEBITO_5', etiqueta: 'IVA débito 5%', monto: 0, condicion: 'Solo si hay IVA al 5%' },
    ],
    nota: 'La cuenta del debe depende del medio de pago de la factura: si configuraste una cuenta para efectivo o transferencia, entra ahí en vez de la caja general. El cheque no se configura por medio: va a "Cheques en cartera", porque la factura no guarda el vencimiento. Una factura contado marcada como cobro diferido se asienta como crédito: la plata entra después, con el recibo.',
  },
  {
    clave: 'VENTA_CREDITO',
    grupo: 'Ventas',
    titulo: 'Venta a crédito',
    cuando: 'Al aprobarse una factura de venta a crédito',
    documento: 'Factura de venta',
    lineas: [
      { lado: 'DEBE', concepto: 'CLIENTES', etiqueta: 'Deuda del cliente', monto: TOTAL },
      { lado: 'HABER', concepto: 'VENTAS_10', etiqueta: 'Venta gravada 10% (sin IVA)', monto: BASE_10 },
      { lado: 'HABER', concepto: 'VENTAS_5', etiqueta: 'Venta gravada 5% (sin IVA)', monto: 0, condicion: 'Solo si la factura tiene ítems al 5%' },
      { lado: 'HABER', concepto: 'VENTAS_EXENTAS', etiqueta: 'Venta exenta', monto: 0, condicion: 'Solo si la factura tiene ítems exentos' },
      { lado: 'HABER', concepto: 'IVA_DEBITO_10', etiqueta: 'IVA débito 10%', monto: IVA_10 },
      { lado: 'HABER', concepto: 'IVA_DEBITO_5', etiqueta: 'IVA débito 5%', monto: 0, condicion: 'Solo si hay IVA al 5%' },
    ],
  },
  {
    clave: 'NOTA_CREDITO_VENTA',
    grupo: 'Ventas',
    titulo: 'Nota de crédito emitida',
    cuando: 'Al aprobarse una nota de crédito de venta (devolución o descuento)',
    documento: 'Nota de crédito de venta',
    lineas: [
      { lado: 'DEBE', concepto: 'VENTAS_10', etiqueta: 'Reversión venta 10%', monto: BASE_10 },
      { lado: 'DEBE', concepto: 'VENTAS_5', etiqueta: 'Reversión venta 5%', monto: 0, condicion: 'Solo si la NC tiene ítems al 5%' },
      { lado: 'DEBE', concepto: 'VENTAS_EXENTAS', etiqueta: 'Reversión venta exenta', monto: 0, condicion: 'Solo si la NC tiene ítems exentos' },
      { lado: 'DEBE', concepto: 'IVA_DEBITO_10', etiqueta: 'Reversión IVA débito 10%', monto: IVA_10 },
      { lado: 'DEBE', concepto: 'IVA_DEBITO_5', etiqueta: 'Reversión IVA débito 5%', monto: 0, condicion: 'Solo si hay IVA al 5%' },
      { lado: 'HABER', concepto: 'CLIENTES', etiqueta: 'Baja de la deuda del cliente', monto: TOTAL },
    ],
  },
  {
    clave: 'COBRO',
    grupo: 'Cobranzas',
    titulo: 'Cobro a un cliente',
    cuando: 'Al registrarse un cobro simple de una factura a crédito',
    documento: 'Recibo de cobro',
    lineas: [
      { lado: 'DEBE', concepto: 'CAJA_GENERAL', fuente: 'MEDIO_PAGO_COBRO', etiqueta: 'Entrada por cobranza', monto: TOTAL },
      { lado: 'DEBE', concepto: 'CHEQUES_EN_CARTERA', etiqueta: 'Parte cobrada con cheque', monto: 0, condicion: 'Solo si parte se cobró con cheque' },
      { lado: 'HABER', concepto: 'CLIENTES', etiqueta: 'Baja de la deuda del cliente', monto: TOTAL },
    ],
    nota: 'La venta ya se asentó al facturar: el cobro no vuelve a tocar ventas ni IVA. Si el recibo mezcla medios, se abre una línea de debe por cuenta. El cheque entra por "Cheques en cartera": este recibo no guarda el vencimiento, así que no puede saber si es diferido.',
  },
  {
    clave: 'RECIBO_MULTI',
    grupo: 'Cobranzas',
    titulo: 'Recibo con varios medios de pago',
    cuando: 'Al confirmarse un recibo que mezcla efectivo, cheques, retenciones o notas de crédito',
    documento: 'Recibo de cobro (multi)',
    lineas: [
      { lado: 'DEBE', concepto: 'CAJA_GENERAL', fuente: 'MEDIO_PAGO_COBRO', etiqueta: 'Parte cobrada en efectivo', monto: 600_000 },
      { lado: 'DEBE', concepto: 'CHEQUES_EN_CARTERA', etiqueta: 'Cheques recibidos al día', monto: 300_000, condicion: 'Solo si el recibo incluye cheques' },
      { lado: 'DEBE', concepto: 'CHEQUES_EN_TRANSITO', etiqueta: 'Cheques diferidos', monto: 0, condicion: 'Solo si el cheque es diferido' },
      { lado: 'DEBE', concepto: 'RETENCION_IVA_FAVOR', etiqueta: 'Retención de IVA que nos hicieron', monto: 100_000, condicion: 'Solo si el cliente retuvo IVA' },
      { lado: 'DEBE', concepto: 'RETENCION_RENTA_FAVOR', etiqueta: 'Retención de renta que nos hicieron', monto: 0, condicion: 'Solo si el cliente retuvo renta' },
      { lado: 'DEBE', concepto: 'NC_APLICADA_PUENTE', etiqueta: 'Nota de crédito aplicada al recibo', monto: 0, condicion: 'Solo si se aplicó una NC' },
      { lado: 'HABER', concepto: 'CLIENTES', etiqueta: 'Baja de la deuda del cliente', monto: 1_000_000 },
      { lado: 'HABER', concepto: 'INTERESES_MORATORIOS_COBRADOS', etiqueta: 'Intereses por mora cobrados', monto: 0, condicion: 'Solo si el recibo cobra intereses' },
      { lado: 'HABER', concepto: 'ANTICIPOS_CLIENTES', etiqueta: 'Excedente que queda a favor del cliente', monto: 0, condicion: 'Solo si se cobró de más' },
    ],
    nota: 'Cada medio de pago entra en su propia cuenta; el total siempre cierra contra la deuda del cliente. Acá sí se conoce el vencimiento del cheque, así que los diferidos van separados de los que están al día.',
  },
  {
    clave: 'COBRO_BANCARD',
    grupo: 'Cobranzas',
    titulo: 'Cobro con tarjeta (Bancard)',
    cuando: 'Al confirmarse un pago con tarjeta procesado por Bancard',
    documento: 'Pago Bancard',
    lineas: [
      { lado: 'DEBE', cuentaDinamica: 'Cuenta "Bancard por liquidar" de la configuración de Bancard', etiqueta: 'Pendiente de liquidación', monto: TOTAL },
      { lado: 'HABER', concepto: 'CLIENTES', etiqueta: 'Baja de la deuda del cliente', monto: TOTAL },
    ],
    nota: 'Sin las cuentas de Bancard configuradas, este asiento no se genera.',
  },

  // ── Compras ───────────────────────────────────────────────────────────────
  {
    clave: 'COMPRA_CONTADO',
    grupo: 'Compras',
    titulo: 'Compra al contado',
    cuando: 'Al confirmarse una compra con condición contado',
    documento: 'Factura de compra',
    lineas: [
      { lado: 'DEBE', concepto: 'INVENTARIO', etiqueta: 'Mercadería o gasto (sin IVA)', monto: BASE_10 },
      { lado: 'DEBE', concepto: 'IVA_CREDITO_10', etiqueta: 'IVA crédito 10%', monto: IVA_10 },
      { lado: 'DEBE', concepto: 'IVA_CREDITO_5', etiqueta: 'IVA crédito 5%', monto: 0, condicion: 'Solo si la compra tiene ítems al 5%' },
      { lado: 'HABER', concepto: 'CAJA_GENERAL', etiqueta: 'Salida de caja', monto: TOTAL },
    ],
  },
  {
    clave: 'COMPRA_CREDITO',
    grupo: 'Compras',
    titulo: 'Compra a crédito',
    cuando: 'Al confirmarse una compra a crédito',
    documento: 'Factura de compra',
    lineas: [
      { lado: 'DEBE', concepto: 'INVENTARIO', etiqueta: 'Mercadería o gasto (sin IVA)', monto: BASE_10 },
      { lado: 'DEBE', concepto: 'IVA_CREDITO_10', etiqueta: 'IVA crédito 10%', monto: IVA_10 },
      { lado: 'DEBE', concepto: 'IVA_CREDITO_5', etiqueta: 'IVA crédito 5%', monto: 0, condicion: 'Solo si la compra tiene ítems al 5%' },
      { lado: 'HABER', cuentaDinamica: 'Cuenta del proveedor; si no tiene una propia, la de PROVEEDORES', etiqueta: 'Deuda con el proveedor', monto: TOTAL },
    ],
  },
  {
    clave: 'NOTA_CREDITO_COMPRA',
    grupo: 'Compras',
    titulo: 'Nota de crédito recibida',
    cuando: 'Al registrarse una nota de crédito del proveedor',
    documento: 'Nota de crédito de compra',
    lineas: [
      { lado: 'DEBE', cuentaDinamica: 'Cuenta del proveedor; si no tiene una propia, la de PROVEEDORES', etiqueta: 'Baja de la deuda con el proveedor', monto: TOTAL },
      { lado: 'HABER', concepto: 'INVENTARIO', etiqueta: 'Reversión de mercadería o gasto', monto: BASE_10 },
      { lado: 'HABER', concepto: 'IVA_CREDITO_10', etiqueta: 'Reversión IVA crédito 10%', monto: IVA_10 },
      { lado: 'HABER', concepto: 'IVA_CREDITO_5', etiqueta: 'Reversión IVA crédito 5%', monto: 0, condicion: 'Solo si la NC tiene ítems al 5%' },
    ],
  },
  {
    clave: 'PAGO_PROVEEDOR',
    grupo: 'Compras',
    titulo: 'Pago a un proveedor',
    cuando: 'Al registrarse un pago de una compra a crédito',
    documento: 'Pago a proveedor',
    lineas: [
      { lado: 'DEBE', concepto: 'PROVEEDORES', etiqueta: 'Baja de la deuda', monto: TOTAL },
      { lado: 'HABER', concepto: 'CAJA_GENERAL', fuente: 'MEDIO_PAGO_PAGO', etiqueta: 'Salida de fondos', monto: TOTAL },
    ],
  },
  {
    clave: 'ORDEN_PAGO',
    grupo: 'Compras',
    titulo: 'Orden de pago',
    cuando: 'Al confirmarse una orden que paga varias facturas juntas',
    documento: 'Orden de pago a proveedor',
    lineas: [
      { lado: 'DEBE', cuentaDinamica: 'Cuenta del proveedor de cada factura incluida', etiqueta: 'Baja de las deudas pagadas', monto: TOTAL },
      { lado: 'HABER', cuentaDinamica: 'Cuenta contable de la cuenta de tesorería usada; si no tiene, CAJA_GENERAL', etiqueta: 'Salida de banco o caja', monto: TOTAL },
    ],
    nota: 'Una orden puede pagar varias facturas y usar varios medios: el asiento lleva una línea por cada uno.',
  },
  {
    clave: 'DESPACHO_IMPORTACION',
    grupo: 'Compras',
    titulo: 'Cierre de despacho de importación',
    cuando: 'Al cerrarse un despacho, con todos los costos prorrateados',
    documento: 'Despacho de importación',
    lineas: [
      { lado: 'DEBE', concepto: 'INVENTARIO', etiqueta: 'Costo de la mercadería importada', monto: BASE_10 },
      { lado: 'DEBE', concepto: 'IVA_CREDITO_10', etiqueta: 'IVA de importación', monto: IVA_10, condicion: 'Solo si el despacho tiene IVA' },
      { lado: 'HABER', cuentaDinamica: 'Cuenta del proveedor del embarque', etiqueta: 'Deuda por la importación', monto: TOTAL },
    ],
  },

  // ── Gastos ────────────────────────────────────────────────────────────────
  {
    clave: 'GASTO',
    grupo: 'Gastos',
    titulo: 'Gasto',
    cuando: 'Al confirmarse un gasto',
    documento: 'Gasto',
    lineas: [
      { lado: 'DEBE', concepto: 'GASTOS_GENERALES', etiqueta: 'Cuenta del tipo de gasto (alquiler, servicios, combustible…)', monto: BASE_10 },
      { lado: 'DEBE', concepto: 'IVA_CREDITO_10', etiqueta: 'IVA crédito 10%', monto: IVA_10, condicion: 'Solo si el gasto tiene comprobante con IVA' },
      { lado: 'DEBE', concepto: 'IVA_CREDITO_5', etiqueta: 'IVA crédito 5%', monto: 0, condicion: 'Solo si el gasto tiene IVA al 5%' },
      { lado: 'HABER', concepto: 'CAJA_GENERAL', etiqueta: 'Contrapartida según cómo se pagó', monto: TOTAL },
    ],
    nota: 'La contrapartida cambia con la forma de pago: caja, BANCOS_DEFAULT si fue por banco, PROVEEDORES si quedó a crédito, o ANTICIPO_PERSONAL si lo puso un empleado.',
  },

  // ── Tesorería ─────────────────────────────────────────────────────────────
  {
    clave: 'MOVIMIENTO_TES_INGRESO',
    grupo: 'Tesorería',
    titulo: 'Ingreso de tesorería',
    cuando: 'Al confirmarse un movimiento de ingreso en una cuenta o caja',
    documento: 'Movimiento de tesorería',
    lineas: [
      { lado: 'DEBE', fuente: 'CUENTA_TESORERIA', cuentaDinamica: 'Cuenta contable de la cuenta de tesorería que recibe', etiqueta: 'Entrada de dinero', monto: TOTAL },
      { lado: 'HABER', fuente: 'CATEGORIA_TESORERIA', cuentaDinamica: 'Cuenta contable de la categoría del movimiento', etiqueta: 'Origen del ingreso', monto: TOTAL },
    ],
    nota: 'Sin cuenta contable en la cuenta de tesorería el asiento no se genera: se configura en Tesorería › Cuentas.',
  },
  {
    clave: 'MOVIMIENTO_TES_EGRESO',
    grupo: 'Tesorería',
    titulo: 'Egreso de tesorería',
    cuando: 'Al confirmarse un movimiento de egreso en una cuenta o caja',
    documento: 'Movimiento de tesorería',
    lineas: [
      { lado: 'DEBE', fuente: 'CATEGORIA_TESORERIA', cuentaDinamica: 'Cuenta contable de la categoría del movimiento', etiqueta: 'Destino del egreso', monto: TOTAL },
      { lado: 'HABER', fuente: 'CUENTA_TESORERIA', cuentaDinamica: 'Cuenta contable de la cuenta de tesorería que paga', etiqueta: 'Salida de dinero', monto: TOTAL },
    ],
  },
  {
    clave: 'TRANSFERENCIA_TES',
    grupo: 'Tesorería',
    titulo: 'Transferencia entre cuentas propias',
    cuando: 'Al confirmarse una transferencia de una cuenta a otra',
    documento: 'Transferencia de tesorería',
    lineas: [
      { lado: 'DEBE', fuente: 'CUENTA_TES_DESTINO', cuentaDinamica: 'Cuenta contable de la cuenta destino', etiqueta: 'Entra en la cuenta destino', monto: TOTAL },
      { lado: 'HABER', fuente: 'CUENTA_TES_ORIGEN', cuentaDinamica: 'Cuenta contable de la cuenta origen', etiqueta: 'Sale de la cuenta origen', monto: TOTAL },
      { lado: 'DEBE', cuentaDinamica: 'Cuenta 6.3.1.03 del plan (no sale del mapeo)', etiqueta: 'Diferencia de cambio perdida', monto: 0, condicion: 'Solo en transferencias en otra moneda, si el tipo de cambio del banco es peor que el del sistema' },
      { lado: 'HABER', cuentaDinamica: 'Cuenta 4.2.1.02 del plan (no sale del mapeo)', etiqueta: 'Diferencia de cambio ganada', monto: 0, condicion: 'Solo en transferencias en otra moneda, si el tipo de cambio del banco es mejor' },
    ],
  },

  // ── Sueldos y comisiones ──────────────────────────────────────────────────
  {
    clave: 'LIQUIDACION_SUELDOS',
    grupo: 'Sueldos y comisiones',
    titulo: 'Liquidación de sueldos',
    cuando: 'Al confirmarse la liquidación del mes',
    documento: 'Liquidación de RRHH',
    lineas: [
      { lado: 'DEBE', concepto: 'SUELDOS_JORNALES', etiqueta: 'Sueldos y jornales del período', monto: 1_000_000 },
      { lado: 'DEBE', concepto: 'APORTE_PATRONAL_IPS', etiqueta: 'Aporte patronal IPS (16,5%)', monto: 165_000 },
      { lado: 'DEBE', concepto: 'APORTE_ADMIN_IPS', etiqueta: 'Aporte administrativo IPS (1%)', monto: 10_000 },
      { lado: 'HABER', concepto: 'SUELDOS_A_PAGAR', etiqueta: 'Neto a pagar al personal', monto: 815_000 },
      { lado: 'HABER', concepto: 'IPS_A_DEPOSITAR', etiqueta: 'IPS a depositar (obrero + patronal + administrativo)', monto: 260_000 },
      { lado: 'HABER', concepto: 'DESCUENTOS_VARIOS_RRHH', etiqueta: 'Descuentos al personal', monto: 100_000, condicion: 'Solo si hay anticipos o descuentos' },
    ],
    nota: 'El debe se reparte por centro de costo según a dónde pertenece cada empleado.',
  },
  {
    clave: 'DEVENGAMIENTO_COMISIONES',
    grupo: 'Sueldos y comisiones',
    titulo: 'Comisiones devengadas — comisionista empleado',
    cuando: 'Al cerrarse la liquidación de un vendedor cuyo régimen es "empleado"',
    documento: 'Liquidación de comisiones',
    lineas: [
      { lado: 'DEBE', concepto: 'GASTO_COMISIONES_VENTAS', etiqueta: 'Gasto por comisiones', monto: TOTAL },
      { lado: 'HABER', concepto: 'COMISIONES_A_PAGAR', etiqueta: 'Comisiones a pagar', monto: TOTAL },
    ],
    nota: 'La comisión de un empleado es remuneración: no lleva IVA ni comprobante. Es también lo que se asienta mientras el vendedor no tenga régimen declarado.',
  },
  {
    clave: 'DEVENGAMIENTO_COMISIONES_IVA_INCLUIDO',
    grupo: 'Sueldos y comisiones',
    titulo: 'Comisiones devengadas — el comisionista factura, IVA incluido',
    cuando: 'Al cerrarse la liquidación de un vendedor externo cuyo porcentaje ya incluye el IVA',
    documento: 'Liquidación de comisiones',
    lineas: [
      { lado: 'DEBE', concepto: 'GASTO_COMISIONES_VENTAS', etiqueta: 'Gasto por comisiones (sin IVA)', monto: 1_000_000 },
      { lado: 'DEBE', concepto: 'IVA_CREDITO_10', etiqueta: 'IVA crédito 10% de la comisión', monto: 100_000 },
      { lado: 'HABER', concepto: 'COMISIONES_A_PAGAR', etiqueta: 'Comisiones a pagar (lo que él factura)', monto: TOTAL },
    ],
    nota: 'El crédito fiscal lo respalda la factura del comisionista, no esta liquidación: el importe liquidado tiene que coincidir con lo que él factura.',
  },
  {
    clave: 'DEVENGAMIENTO_COMISIONES_IVA_ADICIONAL',
    grupo: 'Sueldos y comisiones',
    titulo: 'Comisiones devengadas — el comisionista factura, IVA aparte',
    cuando: 'Al cerrarse la liquidación de un vendedor externo cuyo porcentaje es el honorario neto',
    documento: 'Liquidación de comisiones',
    lineas: [
      { lado: 'DEBE', concepto: 'GASTO_COMISIONES_VENTAS', etiqueta: 'Gasto por comisiones (neto calculado)', monto: TOTAL },
      { lado: 'DEBE', concepto: 'IVA_CREDITO_10', etiqueta: 'IVA crédito 10% sumado encima', monto: 110_000 },
      { lado: 'HABER', concepto: 'COMISIONES_A_PAGAR', etiqueta: 'Comisiones a pagar (neto + IVA)', monto: 1_210_000 },
    ],
    nota: 'Acá se le termina pagando más que el porcentaje calculado, porque el IVA se suma encima.',
  },
  {
    clave: 'PAGO_COMISIONES',
    grupo: 'Sueldos y comisiones',
    titulo: 'Pago de comisiones',
    cuando: 'Al pagarse una liquidación de comisiones',
    documento: 'Liquidación de comisiones',
    lineas: [
      { lado: 'DEBE', concepto: 'COMISIONES_A_PAGAR', etiqueta: 'Cancelación de la deuda', monto: TOTAL },
      { lado: 'HABER', concepto: 'PAGO_COMISIONES_FONDOS', etiqueta: 'Salida de fondos', monto: TOTAL },
    ],
  },
  {
    clave: 'COMISION_SUPERVISOR',
    grupo: 'Sueldos y comisiones',
    titulo: 'Comisión de supervisor',
    cuando: 'Al aprobarse y pagarse una comisión de supervisor',
    documento: 'Comisión de supervisor',
    lineas: [
      { lado: 'DEBE', concepto: 'GASTO_COMISIONES_VENTAS', etiqueta: 'Gasto por comisión', monto: TOTAL },
      { lado: 'HABER', concepto: 'PAGO_COMISIONES_FONDOS', etiqueta: 'Salida de fondos', monto: TOTAL },
    ],
  },
];
